<template>
  <div class="container py-5">

    <div class="level">
      <div class="level-left buttons flex-row mb-0">
        <div class="title is-4 mr-3 ml-1">
          {{ filters.product_brand }} RFQs
        </div>
      </div>

      <div class="level-right buttons">
        <v-fade-transition>
          <button 
            class="button mb-0"
            v-if="selected.length > 0"
            @click="launchConfirmDelete()">
            <span class="icon is-medium">
              <i class="fas fa-trash has-text-danger"></i>
            </span>
            <span>Delete {{ selected.length }} selected RFQs</span>
          </button>
        </v-fade-transition>
        
        <button
          class="button is-primary mb-0"
          @click="addRfq(FULL)">
          <span>  
            Create {{ filters.product_brand }} RFQ
          </span>
        </button>

        <button
          class="button is-primary mb-0"
          @click="addRfq(CONDENSED)">
          <span>  
            Create Condensed {{ filters.product_brand }} RFQ
          </span>
        </button>

        <v-search
          class="mx-2"
          :search-input.sync="filters.project_name"
          @search="setFilter({ field: 'project_name', value: $event });"
          @clear="setFilter({ field: 'project_name', value: null })"
          placeholder="Search RFQs by Project"/>
      </div>
    </div>

    <div class="tabs mb-0 mt-2">
      <ul>
        <li 
          @click="setFilter({ field: 'product_brand', value: 'Mitrex' })"
          :class="{ 'is-active': filters.product_brand == 'Mitrex' }">
          <a>Mitrex</a>
        </li>
        <li 
          @click="setFilter({ field: 'product_brand', value: 'Cladify' })"
          :class="{ 'is-active': filters.product_brand == 'Cladify' }">
          <a>Cladify</a>
        </li>
      </ul>
    </div>

    <v-fade-transition mode="out-in">

      <v-progress v-if="loading" :value="true"/>

      <div class="mt-5" v-else>

        <article 
          v-if="rfqs.length == 0" 
          class="message is-info is-fullwidth">
          <div class="message-body">
            There are no RFQs.
          </div>
        </article>

        <table
          v-else
          class="table is-hoverable is-clickable is-fullwidth 
            has-small-font sticky-header">

          <thead>
            <tr>
              <th class="sticky-header">
                <v-checkbox 
                  :value="allSelected" 
                  @input="toggleAllSelected($event)"/>
              </th>
              <th 
                class="sticky-header"
                v-for="(header, headerIndex) in headers" 
                :key="headerIndex">
                {{ header }}
              </th>
              <th class="sticky-header"></th>
            </tr>
          </thead>

          <tbody>
            <template v-for="project in rfqs">

              <tr 
                class="subheader"
                :key="`project-${project.id}`">
                <td
                  class="has-background-white-ter"
                  :colspan="headers.length + 2">
                  <div class="level my-0">
                    <div class="level-left">
                      <div class="has-text-weight-bold mr-2">
                        {{ project.project_name }}:
                      </div>
                      <div>
                        {{ project.address }}
                      </div>
                    </div>
                    <div class="level-right">
                      <div class="mr-3">
                        <span class="is-size-7">Sales Person:</span>
                        {{ project.crm_object.Owner.name }}
                      </div>
                      <div>
                        <span class="is-size-7">Stage:</span>
                        {{ project.crm_object.Stage }}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>

              <tr 
                class="v-centered"
                v-for="rfq in project.rfqs"
                :key="rfq.id"
                @click="viewRfq(rfq)">

                <td @click.stop="">
                  <v-checkbox 
                    :value="selected.includes(rfq.id)" 
                    @input="toggleSelected(rfq.id)"/>
                </td>

                <td>
                  <span v-if="rfq.id != editingId">
                    {{ rfq.rfq_name }}
                  </span>
                  <v-text-field
                    v-else
                    required
                    v-model="rfq.rfq_name"
                    placeholder="RFQ Name / Version"
                  />
                  {{ rfq.rfq_type }}
                  <span 
                    class="tag is-primary is-light ml-2" 
                    v-if="rfq.rfq_type === CONDENSED"
                  >
                    Condensed RFQ
                  </span>
                </td>

                <td>
                  {{ rfq.version }}
                </td>

                <td>
                  <div class="flex flex--row flex--align-center">
                    <a 
                      v-if="rfq.drawing != null && rfq.id != editingId"
                      @click.stop=""
                      :href="rfq.drawing" 
                      alt="link to drawings"
                      target="_blank">
                      link
                    </a>
                    <v-text-field
                      v-if="rfq.id == editingId"
                      required
                      placeholder="Architectural Drawings"
                      v-model="rfq.drawing"
                      :rules="[
                        v => v.includes('http') ? true : 
                          'Please add https:// in front of the url']"/>
                  </div>
                </td>

                <td>
                  <div class="is-size-7">
                    {{ rfq.created_user.display_name }}
                  </div>
                  <div class="help has-text-grey text--nowrap">
                    {{ formatDate(parseDate(rfq.created_on)) }}
                  </div>
                </td>

                <td>
                  <span v-if="rfq.id != editingId && rfq.main_estimator">
                    {{ rfq.main_estimator.display_name }}
                  </span>
                  <v-select
                    v-if="rfq.id == editingId"
                    required
                    :options="users"
                    item-text="display_name"
                    item-value="id"
                    v-model="rfq.main_estimator_id"
                  />
                </td>

                <td class="is-capitalized">
                  {{ rfq.status.toLowerCase() }}
                </td>

                <td>
                  <button
                    v-if="!rfq.has_drawing_highlights || rfq.drawing_highlights.length == 0"
                    class="button is-small is-primary is-fullwidth"
                    @click.stop="createDrawingHighlight(rfq)">
                    <span class="icon">
                      <i class="fa fa-plus"></i>
                    </span>
                    <span>Request</span>
                  </button>
                  <button
                    v-else
                    class="button is-small is-link is-fullwidth"
                    @click.stop="viewDrawingHighlight(rfq)">
                    <span class="icon">
                      <i class="far fa-eye"></i>
                    </span>
                    <span>View</span>
                  </button>
                </td>

                <td>
                  <button
                    v-if="!rfq.has_c_estimation && !rfq.estimation"
                    class="button is-small is-primary is-fullwidth"
                    :disabled="!rfq.has_drawing_highlights"
                    @click.stop="createEstimation(rfq)">
                    <span class="icon">
                      <i class="fa fa-plus"></i>
                    </span>
                    <span>Create</span>
                  </button>
                  <button
                    v-else
                    class="button is-small is-link is-fullwidth"
                    @click.stop="viewEstimation(rfq)">
                    <span class="icon">
                      <i class="far fa-eye"></i>
                    </span>
                    <span>View</span>
                  </button>
                </td>

                <td>
                  <div class="buttons buttons-right">
                    <button
                      @click.stop="duplicateRfq(rfq)" 
                      class="button is-small">
                      <span class="icon">
                        <i class="fas fa-copy"></i>
                      </span>
                    </button>
                    <button
                      @click.stop="launchConfirmDelete(rfq.id)" 
                      class="button is-small">
                      <span class="icon">
                        <i class="fas fa-trash has-text-grey"></i>
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>

        </table>
      </div>

    </v-fade-transition>

    <duplicate-rfq-modal 
      v-model="duplicating"
      :rfq="duplicatingRfq"/>

    <v-pagination 
      class="mt-3"
      v-if="pagination.pages > 1"
      :value="pagination.page"
      @input="changePage"
      :length="pagination.pages"/>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb ? deleteConfirmedCb() : null"
      @cancel="confirmDelete.state = false"/>

  </div>
</template>

<script>
import VCheckbox from '@/components/base/VCheckbox'
import VTextField from '@/components/base/VTextField'
import VSelect from '@/components/base/VSelect'
import VConfirmModal from '@/components/base/VConfirmModal'
import VPagination from '@/components/base/VPagination'
import VProgress from '@/components/base/VProgress'
import VFadeTransition from '@/components/transitions/VFadeTransition'
import VSearch from '@/components/base/VSearch'
import DuplicateRfqModal from '@/components/rfq/DuplicateRfqModal'
import { CONDENSED, FULL } from '@/helpers/constants'

import { parseJSON, format } from 'date-fns'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VConfirmModal, 
    VPagination, 
    VCheckbox, 
    VTextField,
    VSelect,
    VProgress,
    VSearch,
    VFadeTransition, 
    DuplicateRfqModal
  },
  data() {
    return {
      CONDENSED: CONDENSED,
      FULL: FULL,
      headers: [
        'RFQ Name', 'RFQ Version', 'Drawings', 'Created By/ On', 
        'Main Estimator', 'RFQ Status', 'Drawing Highlight', 'Estimation'
      ],
      adding: false,
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this rfq?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      parseDate: (date) => { return date ? parseJSON(date) : date },
      formatDate: (date) => format(date, "yyyy-MM-dd HH:mm"),
      duplicating: false,
      duplicatingRfq: null,
      editingId: null
    }
  },
  computed: {
    ...mapState("rfqsProjects", [
      "rfqs", "loading", "pagination", 
      "allSelected", "selected", "filters"
    ]),
    ...mapState("users", ["users"])
  },
  watch: {
    'pagination.page'() {
      let params = { ...this.filters, ...this.pagination }
      this.getRfqs(params);
    },
    filters: {
      handler(filters) {
        this.getRfqs(filters);
      },
      deep: true
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("rfqsProjects", [
      "getRfqs", "deleteRfq", "updateRfq", "addEstimation",
      "toggleAllSelected"
    ]),
    ...mapActions("drawingHighlights", ["addDrawingHighlight"]),
    ...mapActions("users", ["getUsers"]),
    ...mapMutations("rfqsProjects", [
      "changePage", "toggleSelected", "setFilter", "addHighlightToRfq"
    ]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      this.getRfqs(this.filters);

      if (this.users.length == 0) {
        this.getUsers();
      }
    },
    launchConfirmDelete(id) {
      if (!id) {
        this.deleteConfirmedCb = () => {
          this.selected.forEach(id => {
            this.deleteRfq(id);
            this.confirmDelete.state = false;
          })
        }
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} RFQs?`;
        this.confirmDelete.state = true;
      } else {
        this.deleteConfirmedCb = () => {
          this.deleteRfq(id);
          this.confirmDelete.state = false;
        }
        this.confirmDelete.message = 'Are you sure you want to delete this RFQ?';
        this.confirmDelete.state = true;
      }
    },
    createDrawingHighlight(rfq) {
      this.addDrawingHighlight({ 
        "project_rfq_id": rfq.id,
        "is_requested": true
      }).then(resp => {
        this.addHighlightToRfq(resp.data);
        this.setNotification({
          status: true,
          type: 'success',
          message: 'Drawing highlight successfully requested'
        });
      })
    },
    viewDrawingHighlight(rfq) {
      let highlight = rfq.drawing_highlights[0];
      highlight.project_rfq = rfq;
      this.$router.push({
        name: 'DrawingHighlightViewer', 
        params: {
          drawingHighlightId: highlight.id
        } 
      })
    },
    createEstimation(rfq) {
      const { rfqs } = this;

      this.addEstimation({
        project_rfq_id: rfq.id
      }).then(() => {
        this.viewEstimation(
          rfqs.map(d => d.rfqs).flat(1).filter(d => d.id == rfq.id)[0]
        );
      })
    },
    viewEstimation(rfq) {
      if (!rfq.c_estimation) {
        this.$router.push({
          name: 'EstimationViewer', 
          params: {
            estimationId: rfq.estimation.id
          } 
        })
      } else {
        if (this.filters.product_brand == 'Cladify') {
          let estimation = rfq.c_estimation;
          estimation.rfq = rfq;

          this.$router.push({
            name: 'EstimationViewerCladify',
            params: {
              estimationId: rfq.c_estimation.id
            }
          })
        } else {
          let estimation = rfq.c_estimation;
          estimation.rfq = rfq;

          this.$router.push({
            name: 'EstimationViewerMitrex',
            params: {
              estimationId: rfq.c_estimation.id
            }
          })
        }
      }
    },
    addRfq(rfqType) {
      this.$router.push({ 
        name: 'RfqMaker', 
        params: { 
          productBrand: this.filters.product_brand,
          data: { rfqType: rfqType }
        } 
      });
    },
    viewRfq(rfq) {
      this.$router.push({ 
        name: 'RfqViewer',
        params: {
          productBrand: this.filters.product_brand,
          rfqId: rfq.id
        }
      });
    },
    duplicateRfq(rfq) {
      this.duplicatingRfq = rfq;
      this.duplicating = true;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';
</style>